<script>
import { mapGetters } from 'vuex';
import dynamicField from '../dynamicField/Index.vue';
import InboundWebhookContent from './InboundWebhookContent.vue';
import InputMappingContent from './InputMappingContent.vue';
import SidebarHeader from './SidebarHeader.vue';

export default {
  components: {
    DynamicField: dynamicField,
    InputMappingContent,
    InboundWebhookContent,
    SidebarHeader,
  },
  props: {
    trigger: {
      type: Object,
      default: () => {},
    },
    disableTriggerChange: {
      type: Boolean,
      default: false,
    },
    disableTriggerAttributes: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    dirty: {
      type: Boolean,
      default: false,
    },
    variables: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['triggerChange'],
  data() {
    return {
      updatedType: this.trigger.type,
    };
  },
  computed: {
    availableVariables() {
      return this.variables.filter(variable => {
        if (variable.source) return true;
        if (variable.wrapper) return true;
        if (!variable.model) return true;
        return false;
      });
    },
    ...mapGetters({
      triggers: 'wgptAutomationRegistry/getTriggers',
    }),
    triggerRegistry() {
      if (!this.updatedType) return {};
      return (
        this.$store.getters['wgptAutomationRegistry/getTrigger'](
          this.updatedType
        ) ?? {}
      );
    },
    fields() {
      return this.triggerRegistry.form_fields || [];
    },
    attributes() {
      return this.trigger.attributes || {};
    },
    triggerOptions() {
      const triggerRegistry = this.triggers[this.trigger.type] || {};
      if (triggerRegistry.managed) {
        return [
          {
            label: triggerRegistry.label,
            value: this.trigger.type,
          },
        ];
      }

      return Object.entries(this.triggers)
        .filter(predicate => !predicate[1].managed && !predicate[1].hidden)
        .map(([key, trigger]) => {
          return {
            // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
            label: trigger.label,
            value: key,
          };
        });
    },
    triggerField() {
      return {
        type: 'select',
        required: true,
        options: this.triggerOptions,
        placeholder: 'FORM.TRIGGER_FIELD.PLACEHOLDER',
      };
    },
    inputMapping() {
      return this.trigger.input_mapping || {};
    },
    inputMappingFields() {
      return this.triggerRegistry.input_mapping_fields || [];
    },
    hasInputMappingFields() {
      return this.inputMappingFields.length > 0;
    },
    isInboundWebhookType() {
      return this.updatedType === 'inbound_webhook';
    },
  },
  watch: {
    trigger(trigger) {
      if (trigger.type !== this.updatedType) {
        this.updatedType = trigger.type;
      }
    },
  },
  methods: {
    onChange(triggerName) {
      const trigger = this.triggers[triggerName];
      const triggerUpdates = {
        type: triggerName,
        label: trigger.label,
      };

      const formFields = trigger.form_fields ?? [];
      formFields.forEach(field => {
        if (Object.keys(field).includes('default')) {
          const triggerAttributesBefore = triggerUpdates.attributes ?? {};
          triggerUpdates.attributes = {
            ...triggerAttributesBefore,
            [field.name]: field.default,
          };
        }
      });

      this.$emit('triggerChange', triggerUpdates);
    },
    isVisible(field) {
      const conditions = field.show_if ?? [];
      const results = conditions.map(({ field: fieldName, value }) => {
        const currentValue =
          this.resolveFieldValue(this.attributes, fieldName) ??
          this.getDefaultFieldValue(fieldName);
        if (currentValue == null) return false;
        const values = Array.isArray(value) ? value : [value];
        return values.some(v => {
          if (v.includes('*'))
            return `${currentValue}`.includes(v.replace(/\*/g, ''));
          return currentValue === v;
        });
      });
      return results.every(result => result);
    },
    getDefaultFieldValue(fieldName) {
      const field = this.fields.find(({ name }) => name === fieldName);
      return field?.default;
    },
    resolveFieldValue(obj, fieldName) {
      return fieldName.split('.').reduce((prev, curr) => {
        if (prev == null) return prev;
        return prev[curr];
      }, obj);
    },
    onFieldChange(key, value) {
      const attributesBefore = this.trigger?.attributes ?? {};
      const trigger = {
        attributes: {
          ...attributesBefore,
          [key]: value,
        },
      };
      this.$emit('triggerChange', trigger);
    },
    onInputMappingFieldChange(inputMapping) {
      const trigger = {
        input_mapping: inputMapping,
      };
      this.$emit('triggerChange', trigger);
    },
  },
};
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <div v-if="trigger" class="p-6 flex flex-col gap-6">
    <SidebarHeader
      :header="$t('WGPT_AUTOMATIONS.EDITOR.SIDEBAR.TRIGGER.TITLE')"
      :sub-header="$t('WGPT_AUTOMATIONS.EDITOR.SIDEBAR.TRIGGER.DESCRIPTION')"
    />

    <DynamicField
      :field="triggerField"
      :disabled="disableTriggerChange"
      :dirty="dirty"
      :value="updatedType"
      :show-variables-selector="false"
      @change="onChange"
    />

    <InboundWebhookContent v-if="isInboundWebhookType" />

    <div class="custom-fields flex flex-col gap-6">
      <DynamicField
        v-for="(field, index) of fields"
        :key="index"
        :field="field"
        :disabled="disabled || disableTriggerAttributes"
        :dirty="dirty"
        :value="attributes[field.name]"
        :visible="isVisible(field)"
        :show-variables-selector="false"
        @change="value => onFieldChange(field.name, value)"
      />
    </div>

    <template v-if="hasInputMappingFields">
      <div class="border-t border-n-weak" />

      <InputMappingContent
        :input-mapping="inputMapping"
        :input-mapping-fields="inputMappingFields"
        :variables="availableVariables"
        :dirty="dirty"
        @change="onInputMappingFieldChange"
      />
    </template>
  </div>
</template>
